package com.dotdrives.web.pages.drives

import com.dotdrives.controller.DriveDetailController
import com.dotdrives.controller.DrivesBoardController
import com.dotdrives.controller.NewDriveController
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.OrgMember
import com.dotdrives.repository.SupabaseDriveRepository
import com.dotdrives.repository.SupabaseGiverRepository
import com.dotdrives.repository.SupabaseOrgRepository
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.shell.PortalShell
import com.dotdrives.web.ui.useFlowState
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.useEffect
import react.useState

private class DrivesControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val board: DrivesBoardController,
    val detail: DriveDetailController,
    val newDrive: NewDriveController,
    val reminder: com.dotdrives.controller.NewReminderController,
    val quickRecord: com.dotdrives.controller.QuickRecordController,
    val orgRepository: SupabaseOrgRepository,
)

external interface DrivesPortalProps : Props {
    var initialDriveId: String?
    var initialNewGiverId: String?
    var initialTemplateId: String?
    var initialPhaseId: String?
    var openNew: Boolean
    var orgId: String
    var userId: String
    var userInitials: String
    var userName: String
    var orgName: String
    var canWrite: Boolean
    var currentUserRole: String
    var onSwitchOrg: () -> Unit
    var onNavigate: (NavItem) -> Unit
    var onSignOut: () -> Unit
}

val DrivesPortal = FC<DrivesPortalProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        val driveRepo = SupabaseDriveRepository()
        val giverRepo = SupabaseGiverRepository()
        val orgRepository = SupabaseOrgRepository()
        DrivesControllerHolder(
            scope = scope,
            board = DrivesBoardController(driveRepo, scope, props.orgId),
            detail = DriveDetailController(driveRepo, giverRepo, scope, props.orgId),
            newDrive = NewDriveController(
                giverRepo = giverRepo,
                driveRepo = driveRepo,
                scope = scope,
                orgId = props.orgId,
                defaultStewardUserId = props.userId,
            ),
            reminder = com.dotdrives.controller.NewReminderController(com.dotdrives.repository.SupabaseReminderRepository(), scope, props.orgId, props.userId),
            quickRecord = com.dotdrives.controller.QuickRecordController(giverRepo, com.dotdrives.repository.SupabaseDonationRepository(), scope, props.orgId),
            orgRepository = orgRepository,
        )
    }
    val giftWorkflow = com.dotdrives.web.ui.useWorkflowController(props.userId, props.orgId, props.currentUserRole, "manual-gift")
    val giftWorkflowState = useFlowState(giftWorkflow.state)
    val moveWorkflow = com.dotdrives.web.ui.useWorkflowController(props.userId, props.orgId, props.currentUserRole, "drive-move")
    val moveState = useFlowState(moveWorkflow.state)
    val reminderState = useFlowState(holder.reminder.state)
    val quickState = useFlowState(holder.quickRecord.state)
    val boardState = useFlowState(holder.board.state)
    val detailState = useFlowState(holder.detail.state)
    val newDriveState = useFlowState(holder.newDrive.state)
    val (initialGiver, setInitialGiver) = useState<GiverSummary?>(null)
    val (members, setMembers) = useState<List<OrgMember>>(emptyList())

    useEffect(holder) {
        holder.scope.launch {
            holder.orgRepository.members(props.orgId).onSuccess { setMembers(it) }
        }
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    useEffect(props.initialNewGiverId) {
        setInitialGiver(null)
        props.initialNewGiverId?.let { id ->
            SupabaseGiverRepository().get(props.orgId, id).onSuccess { giver ->
                setInitialGiver(GiverSummary(giver.id, giver.displayName, giver.giverType, giver.stewardUserId, null, giver.temperature, giver.tags))
            }
        }
    }
    PortalShell {
        active = NavItem.Drives
        onNavigate = props.onNavigate
        userInitials = props.userInitials
        userName = props.userName
        orgName = props.orgName
        onSwitchOrg = props.onSwitchOrg
        onSignOut = props.onSignOut

        DrivesPage {
            giftController = giftWorkflow
            giftState = giftWorkflowState
            orgId = props.orgId
            initialDriveId = props.initialDriveId
            initialNewGiverId = props.initialNewGiverId
            initialNewGiver = initialGiver?.takeIf { it.id == props.initialNewGiverId }
            openNew = props.openNew
            initialTemplateId = props.initialTemplateId
            initialPhaseId = props.initialPhaseId
            moveController = moveWorkflow
            moveWorkflowState = moveState
            reminderController = holder.reminder
            this.reminderState = reminderState
            currentUserName = props.userName
            quickRecordController = holder.quickRecord
            quickRecordState = quickState
            boardController = holder.board
            this.boardState = boardState
            detailController = holder.detail
            this.detailState = detailState
            newDriveController = holder.newDrive
            this.newDriveState = newDriveState
            this.members = members
            currentUserId = props.userId
            canWrite = props.canWrite
        }
    }
}
